// 总后台 HTTP 客户端 — 复用 admin-reviewer 同款协议,端独立(可单独停用)
// 开发默认指向本机 Laravel;生产用 .env 的 VITE_API_BASE 覆盖。
const BASE = import.meta.env.VITE_API_BASE ?? 'http://wx.io/api/v1';

const K_TOKEN = 'mba-platform:token';
const K_ADMIN = 'mba-platform:admin';

export interface AdminInfo {
  id: string;
  username: string;
  displayName: string | null;
  role: 'REVIEWER' | 'PLATFORM_ADMIN';
}

export interface ApiError {
  code: string;
  message: string;
  httpStatus: number;
}

export function getToken(): string | null {
  return localStorage.getItem(K_TOKEN);
}
export function setAuth(token: string, admin: AdminInfo): void {
  localStorage.setItem(K_TOKEN, token);
  localStorage.setItem(K_ADMIN, JSON.stringify(admin));
}
export function getAdmin(): AdminInfo | null {
  const raw = localStorage.getItem(K_ADMIN);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminInfo;
  } catch {
    return null;
  }
}
export function clearAuth(): void {
  localStorage.removeItem(K_TOKEN);
  localStorage.removeItem(K_ADMIN);
}

export async function request<T = unknown>(opts: {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  body?: unknown;
  auth?: boolean;
}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.auth !== false) {
    const t = getToken();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }
  const res = await fetch(`${BASE}${opts.path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.ok) {
    return (await res.json()) as T;
  }
  let body: Partial<ApiError> = {};
  try {
    body = (await res.json()) as Partial<ApiError>;
  } catch {
    // ignore
  }
  if (res.status === 401) clearAuth();
  throw {
    code: body.code ?? `HTTP_${res.status}`,
    message: body.message ?? `请求失败(${res.status})`,
    httpStatus: res.status,
  } satisfies ApiError;
}

// ========== auth ==========
export async function adminLogin(username: string, password: string) {
  const r = await request<{ token: string; admin: AdminInfo }>({
    method: 'POST',
    path: '/admin/auth/login',
    body: { username, password },
    auth: false,
  });
  setAuth(r.token, r.admin);
  return r.admin;
}
export async function adminMe(): Promise<AdminInfo> {
  return request<AdminInfo>({ path: '/admin/auth/me' });
}

// ========== users ==========
export interface UserRow {
  id: string;
  openid: string;
  displayName: string | null;
  schoolName: string | null;
  enrollYear: number | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'FROZEN';
  roles: string[];
  invitedCount: number;
  createdAt: string;
  lastLoginAt: string | null;
  deletedAt: string | null;
}
export async function listUsers(params: {
  status?: string;
  q?: string;
  take?: number;
  skip?: number;
  includeBanned?: boolean;
}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.take != null) qs.set('take', String(params.take));
  if (params.skip != null) qs.set('skip', String(params.skip));
  if (params.includeBanned) qs.set('includeBanned', '1');
  return request<{ total: number; items: UserRow[] }>({
    path: `/admin/users?${qs.toString()}`,
  });
}
export async function freezeUser(id: string, reason?: string) {
  return request({ method: 'POST', path: `/admin/users/${id}/freeze`, body: { reason } });
}
export async function unfreezeUser(id: string) {
  return request({ method: 'POST', path: `/admin/users/${id}/unfreeze` });
}
export async function banUser(id: string, reason?: string) {
  return request({ method: 'POST', path: `/admin/users/${id}/ban`, body: { reason } });
}
export async function unbanUser(id: string) {
  return request({ method: 'POST', path: `/admin/users/${id}/unban` });
}

// ========== admins (审核员/管理员) ==========
export interface AdminRow {
  id: string;
  username: string;
  displayName: string | null;
  role: 'REVIEWER' | 'PLATFORM_ADMIN';
  status: 'ACTIVE' | 'DISABLED';
  lastLoginAt: string | null;
  createdAt: string;
}
export async function listAdmins() {
  return request<AdminRow[]>({ path: '/admin/admins' });
}
export async function createAdmin(input: {
  username: string;
  password: string;
  role: 'REVIEWER' | 'PLATFORM_ADMIN';
  displayName?: string;
}) {
  return request({ method: 'POST', path: '/admin/admins', body: input });
}
export async function setAdminStatus(id: string, status: 'ACTIVE' | 'DISABLED') {
  return request({ method: 'PATCH', path: `/admin/admins/${id}/status`, body: { status } });
}
export async function removeAdmin(id: string) {
  return request({ method: 'DELETE', path: `/admin/admins/${id}` });
}

// ========== plaza posts (广场管理) ==========
export interface PostAuthorBrief {
  id: string;
  displayName: string | null;
  avatarUrl: string | null;
  schoolName: string | null;
  enrollYear: number | null;
  status: string;
}
export interface PostMediaItem {
  id: string;
  url: string;
  mimeType: string | null;
  safetyLevel: string;
  safetyReason: string | null;
  sortOrder: number;
}
export interface PostRow {
  id: string;
  text: string;
  visibleStatus: 'VISIBLE' | 'HIDDEN_PENDING_REVIEW' | 'BLOCKED';
  visibleScope: string;
  likeCount: number;
  commentCount: number;
  createdAt: string;
  author: PostAuthorBrief | null;
  media: PostMediaItem[];
}
export async function listPosts(params: {
  status?: string;
  q?: string;
  take?: number;
  skip?: number;
}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.take != null) qs.set('take', String(params.take));
  if (params.skip != null) qs.set('skip', String(params.skip));
  return request<{ total: number; items: PostRow[] }>({
    path: `/admin/posts?${qs.toString()}`,
  });
}
export async function takedownPost(id: string, reason?: string) {
  return request<{ ok: true; postId: string; from: string; to: string }>({
    method: 'POST',
    path: `/admin/posts/${id}/takedown`,
    body: reason ? { reason } : undefined,
  });
}
export async function restorePost(id: string) {
  return request<{ ok: true; postId: string; from: string; to: string }>({
    method: 'POST',
    path: `/admin/posts/${id}/restore`,
  });
}

// ========== audit logs ==========
export interface AuditLogRow {
  id: string;
  actorAdminId: string;
  actorAdminUsername: string | null;
  targetDomain: 'CERTIFICATION' | 'ROLE_APPLICATION' | 'USER';
  targetId: string;
  action: 'APPROVE' | 'REJECT' | 'FREEZE' | 'UNFREEZE';
  detail: Record<string, unknown> | null;
  createdAt: string;
}
export async function listAuditLogs(params: {
  targetDomain?: string;
  action?: string;
  actorAdminId?: string;
  targetId?: string;
  take?: number;
  skip?: number;
}) {
  const qs = new URLSearchParams();
  if (params.targetDomain) qs.set('targetDomain', params.targetDomain);
  if (params.action) qs.set('action', params.action);
  if (params.actorAdminId) qs.set('actorAdminId', params.actorAdminId);
  if (params.targetId) qs.set('targetId', params.targetId);
  if (params.take != null) qs.set('take', String(params.take));
  if (params.skip != null) qs.set('skip', String(params.skip));
  return request<{ total: number; items: AuditLogRow[] }>({
    path: `/admin/audit-logs?${qs.toString()}`,
  });
}

// ========== 广告位(RFC-006) ==========
export type AdPlacement = 'HOME' | 'PLAZA' | 'MFRIENDS' | 'PROFILE';
export type AdLinkType = 'NONE' | 'MINIPROGRAM' | 'H5';

export interface AdRow {
  id: string;
  placement: AdPlacement;
  title: string | null;
  imageUrl: string;
  imageKey: string;
  linkType: AdLinkType;
  linkValue: string | null;
  sort: number;
  active: boolean;
  createdAt: string;
}

export interface AdPayload {
  placement: AdPlacement;
  title?: string | null;
  imageUrl: string;
  imageKey: string;
  linkType: AdLinkType;
  linkValue?: string | null;
  sort: number;
  active: boolean;
}

export async function listAds(placement?: AdPlacement) {
  const qs = placement ? `?placement=${placement}` : '';
  return request<{ items: AdRow[] }>({ path: `/admin/ads${qs}` });
}

/** 上传广告图(base64 JSON 模式,复用后端双模式上传)。返回 {url,key}。 */
export async function uploadAdImage(dataUrl: string, mime: string, name: string) {
  return request<{ url: string; key: string }>({
    method: 'POST',
    path: '/admin/ads/upload',
    body: { image: { data: dataUrl, mime, name } },
  });
}

export async function createAd(payload: AdPayload) {
  return request<AdRow>({ method: 'POST', path: '/admin/ads', body: payload });
}

export async function updateAd(id: string, payload: Partial<AdPayload>) {
  return request<AdRow>({ method: 'PUT', path: `/admin/ads/${id}`, body: payload });
}

export async function deleteAd(id: string) {
  return request<{ ok: boolean }>({ method: 'DELETE', path: `/admin/ads/${id}` });
}

/** 广告图相对路径拼绝对地址(用于后台预览) */
export function adImageAbsUrl(url: string): string {
  if (/^https?:\/\//.test(url)) return url;
  const origin = BASE.replace(/\/api\/v1\/?$/, '');
  return `${origin}${url}`;
}

// ========== 头条 / 训练营发布台(RFC-008) ==========

export type ChannelType = 'HEADLINE' | 'TRAINING_CAMP';
export type ChannelPostStatus = 'DRAFT' | 'PUBLISHED';

export interface ChannelPostRow {
  id: string;
  channel: ChannelType;
  title: string;
  body: string;
  coverUrl: string | null;
  coverKey: string | null;
  status: ChannelPostStatus;
  signupEnabled: boolean;
  signupCount: number;
  sort: number;
  publishedAt: string | null;
  createdAt: string;
}

export interface ChannelPostPayload {
  channel: ChannelType;
  title: string;
  body: string;
  coverUrl?: string | null;
  coverKey?: string | null;
  status: ChannelPostStatus;
  signupEnabled?: boolean;
  sort?: number;
}

export interface ChannelSignupRow {
  id: string;
  userId: string;
  name: string;
  mobile: string;
  note: string | null;
  createdAt: string;
}

export async function listChannelPosts(channel?: ChannelType) {
  const qs = channel ? `?channel=${channel}` : '';
  return request<{ items: ChannelPostRow[] }>({ path: `/admin/channel-posts${qs}` });
}

/** 上传封面(base64 JSON,复用后端双模式)。返回 {url,key}。 */
export async function uploadChannelCover(dataUrl: string, mime: string, name: string) {
  return request<{ url: string; key: string }>({
    method: 'POST',
    path: '/admin/channel-posts/upload',
    body: { image: { data: dataUrl, mime, name } },
  });
}

export async function createChannelPost(payload: ChannelPostPayload) {
  return request<ChannelPostRow>({ method: 'POST', path: '/admin/channel-posts', body: payload });
}

export async function updateChannelPost(id: string, payload: Partial<ChannelPostPayload>) {
  return request<ChannelPostRow>({ method: 'PUT', path: `/admin/channel-posts/${id}`, body: payload });
}

export async function deleteChannelPost(id: string) {
  return request<{ ok: boolean }>({ method: 'DELETE', path: `/admin/channel-posts/${id}` });
}

export async function listChannelSignups(id: string) {
  return request<{ items: ChannelSignupRow[] }>({ path: `/admin/channel-posts/${id}/signups` });
}

// ===== 数据统计(只读) =====

export interface StatsOverview {
  totals: {
    users: number;
    approvedUsers: number;
    posts: number;
    comments: number;
    tribes: number;
    channelPosts: number;
    messages: number;
    pendingReports: number;
  };
  userAnalysis: {
    byStatus: { PENDING: number; APPROVED: number; REJECTED: number; FROZEN: number };
    certifiedRate: number;
    newToday: number;
    newThisWeek: number;
    topSchools: Array<{ name: string; count: number }>;
  };
  contentAnalysis: {
    visiblePosts: number;
    pendingPosts: number;
    blockedPosts: number;
    tribePosts: number;
  };
}

export interface StatsTrends {
  days: number;
  labels: string[];
  series: Array<{ key: string; name: string; data: number[] }>;
}

export interface RecentMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string | null;
  type: string;
  content: string;
  createdAt: string;
}

export async function getStatsOverview() {
  return request<StatsOverview>({ path: '/admin/stats/overview' });
}

export async function getStatsTrends(days: 7 | 14 | 30 = 7) {
  return request<StatsTrends>({ path: `/admin/stats/trends?days=${days}` });
}

export async function getRecentMessages(take = 30) {
  return request<{ items: RecentMessage[] }>({ path: `/admin/stats/messages?take=${take}` });
}
